import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import QRCode from 'qrcode';
import {
  Container, TextField, Paper, Typography, Box,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch,
  FormHelperText, Chip, IconButton, Button, Menu, Divider,
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

interface QROptions {
  color: string;
  backgroundColor: string;
  transparent: boolean;
  borderStyle: string;
  borderWidth: number;
  borderColor: string;
}

interface AppProps {
  darkMode: boolean;
  onDarkModeToggle: () => void;
}

interface Preset {
  name: string;
  color: string;
  backgroundColor: string;
  borderStyle: string;
  borderColor: string;
}

const defaultOptions: QROptions = {
  color: '#000000',
  backgroundColor: '#ffffff',
  transparent: false,
  borderStyle: 'solid',
  borderWidth: 20,
  borderColor: '#000000',
};

const presets: Preset[] = [
  { name: 'Classic', color: '#000000', backgroundColor: '#ffffff', borderStyle: 'solid', borderColor: '#000000' },
  { name: 'Ocean', color: '#006994', backgroundColor: '#e0f7fa', borderStyle: 'solid', borderColor: '#006994' },
  { name: 'Sunset', color: '#ff6f00', backgroundColor: '#fff3e0', borderStyle: 'dashed', borderColor: '#ff6f00' },
  { name: 'Forest', color: '#2e7d32', backgroundColor: '#e8f5e9', borderStyle: 'solid', borderColor: '#2e7d32' },
  { name: 'Lavender', color: '#7b1fa2', backgroundColor: '#f3e5f5', borderStyle: 'dotted', borderColor: '#7b1fa2' },
  { name: 'Rose Gold', color: '#b76e79', backgroundColor: '#fce4ec', borderStyle: 'solid', borderColor: '#b76e79' },
  { name: 'Midnight', color: '#1a237e', backgroundColor: '#000000', borderStyle: 'none', borderColor: '#1a237e' },
  { name: 'Candy', color: '#e91e63', backgroundColor: '#ffebee', borderStyle: 'double', borderColor: '#e91e63' },
  { name: 'Mint', color: '#00897b', backgroundColor: '#e0f2f1', borderStyle: 'dashed', borderColor: '#00897b' },
  { name: 'Halloween', color: '#ff5722', backgroundColor: '#212121', borderStyle: 'dotted', borderColor: '#ff5722' },
  { name: 'Valentine', color: '#c62828', backgroundColor: '#fce4ec', borderStyle: 'solid', borderColor: '#c62828' },
  { name: 'Neon', color: '#00ff00', backgroundColor: '#000000', borderStyle: 'solid', borderColor: '#00ff00' },
];

function getUrlParams(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

function App({ darkMode, onDarkModeToggle }: AppProps): JSX.Element {
  const params = getUrlParams();
  
  const [url, setUrl] = useState<string>('https://example.com');
  const [qrCodeImage, setQrCodeImage] = useState<string>('');
  const [options, setOptions] = useState<QROptions>({
    color: params.get('color') || defaultOptions.color,
    backgroundColor: params.get('bg') || defaultOptions.backgroundColor,
    transparent: params.get('transparent') === 'true',
    borderStyle: params.get('borderStyle') || defaultOptions.borderStyle,
    borderWidth: parseInt(params.get('borderWidth') || '') || defaultOptions.borderWidth,
    borderColor: params.get('borderColor') || defaultOptions.borderColor,
  });

  const updateUrlParams = useCallback((newOptions: QROptions) => {
    const urlParams = new URLSearchParams();
    urlParams.set('color', newOptions.color);
    urlParams.set('bg', newOptions.backgroundColor);
    urlParams.set('transparent', String(newOptions.transparent));
    urlParams.set('borderStyle', newOptions.borderStyle);
    urlParams.set('borderWidth', String(newOptions.borderWidth));
    urlParams.set('borderColor', newOptions.borderColor);
    
    const newUrlStr = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.replaceState({}, '', newUrlStr);
  }, []);

  const generateQRCode = useCallback((): void => {
    if (!url) return;

    QRCode.toDataURL(url, {
      width: 800,
      margin: 2,
      errorCorrectionLevel: 'H',
      color: {
        dark: options.color,
        light: options.transparent ? '#00000000' : options.backgroundColor,
      },
    }, (err: Error | null | undefined, url: string) => {
      if (err) console.error(err);
      else {
        const canvas = document.createElement('canvas');
        const img = new Image();
        img.onload = () => {
          canvas.width = img.width + options.borderWidth * 2;
          canvas.height = img.height + options.borderWidth * 2;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            if (!options.transparent) {
              ctx.fillStyle = options.backgroundColor;
              ctx.fillRect(0, 0, canvas.width, canvas.height);
            }
            
            if (options.borderStyle !== 'none') {
              ctx.strokeStyle = options.borderColor;
              ctx.lineWidth = options.borderWidth;
              
              if (options.borderStyle === 'dashed') {
                ctx.setLineDash([options.borderWidth * 2, options.borderWidth]);
              } else if (options.borderStyle === 'dotted') {
                ctx.setLineDash([options.borderWidth, options.borderWidth]);
              } else if (options.borderStyle === 'double') {
                const offset = options.borderWidth / 2;
                ctx.strokeRect(
                  options.borderWidth / 2 + offset,
                  options.borderWidth / 2 + offset,
                  canvas.width - options.borderWidth - offset * 2,
                  canvas.height - options.borderWidth - offset * 2
                );
                ctx.strokeRect(
                  options.borderWidth / 2 + options.borderWidth * 2,
                  options.borderWidth / 2 + options.borderWidth * 2,
                  canvas.width - options.borderWidth * 5,
                  canvas.height - options.borderWidth * 5
                );
              }
              
              if (options.borderStyle === 'solid' || options.borderStyle === 'dashed' || options.borderStyle === 'dotted') {
                ctx.strokeRect(
                  options.borderWidth / 2,
                  options.borderWidth / 2,
                  canvas.width - options.borderWidth,
                  canvas.height - options.borderWidth
                );
              }
            }
            
            ctx.drawImage(img, options.borderWidth, options.borderWidth);
            setQrCodeImage(canvas.toDataURL('image/png'));
          }
        };
        img.src = url;
      }
    });
  }, [url, options]);

  useEffect(() => {
    generateQRCode();
    updateUrlParams(options);
  }, [generateQRCode, updateUrlParams, options]);

  const handleUrlChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setUrl(e.target.value);
  };

  const handleOptionChange = (field: keyof QROptions, value: string | number | boolean): void => {
    setOptions(prev => ({ ...prev, [field]: value }));
  };

  const applyPreset = (preset: Preset): void => {
    setOptions(prev => ({
      ...prev,
      color: preset.color,
      backgroundColor: preset.backgroundColor,
      borderStyle: preset.borderStyle,
      borderColor: preset.borderColor,
      transparent: false,
    }));
  };

  const handleReset = (): void => {
    setOptions(defaultOptions);
    window.history.replaceState({}, '', window.location.pathname);
  };

  const [saveMenuAnchor, setSaveMenuAnchor] = useState<null | HTMLElement>(null);

  const triggerDownload = (href: string, filename: string, revoke = false): void => {
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    if (revoke) URL.revokeObjectURL(href);
  };

  const downloadAs = (format: 'png' | 'jpeg' | 'webp' | 'svg'): void => {
    setSaveMenuAnchor(null);
    if (!qrCodeImage && format !== 'svg') return;

    if (format === 'png') {
      triggerDownload(qrCodeImage, 'qr-code.png');
      return;
    }

    if (format === 'svg') {
      QRCode.toString(url, {
        type: 'svg',
        margin: 2,
        errorCorrectionLevel: 'H',
        color: {
          dark: options.color,
          light: options.transparent ? '#00000000' : options.backgroundColor,
        },
      } as any, (err: Error | null | undefined, svgStr: string) => {
        if (err) return;

        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgStr, 'image/svg+xml');
        const svgEl = svgDoc.querySelector('svg');
        if (!svgEl) return;

        if (options.borderStyle !== 'none') {
          const [, , vbW, vbH] = (svgEl.getAttribute('viewBox') ?? '0 0 41 41').split(' ').map(Number);
          const bw = (options.borderWidth / 400) * vbW;

          svgEl.setAttribute('viewBox', `${-bw} ${-bw} ${vbW + bw * 2} ${vbH + bw * 2}`);

          const border = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
          border.setAttribute('x', String(-bw / 2));
          border.setAttribute('y', String(-bw / 2));
          border.setAttribute('width', String(vbW + bw));
          border.setAttribute('height', String(vbH + bw));
          border.setAttribute('fill', 'none');
          border.setAttribute('stroke', options.borderColor);
          border.setAttribute('stroke-width', String(bw));

          if (options.borderStyle === 'dashed') {
            border.setAttribute('stroke-dasharray', `${bw * 2} ${bw}`);
          } else if (options.borderStyle === 'dotted') {
            border.setAttribute('stroke-dasharray', `${bw * 0.5} ${bw}`);
            border.setAttribute('stroke-linecap', 'round');
          } else if (options.borderStyle === 'double') {
            const inner = border.cloneNode() as SVGRectElement;
            const gap = bw * 0.4;
            inner.setAttribute('x', String(bw / 2 + gap));
            inner.setAttribute('y', String(bw / 2 + gap));
            inner.setAttribute('width', String(vbW - bw - gap * 2));
            inner.setAttribute('height', String(vbH - bw - gap * 2));
            inner.setAttribute('stroke-width', String(bw * 0.4));
            svgEl.appendChild(inner);
            border.setAttribute('stroke-width', String(bw * 0.4));
          }

          svgEl.appendChild(border);
        }

        const blob = new Blob([svgEl.outerHTML], { type: 'image/svg+xml' });
        const blobUrl = URL.createObjectURL(blob);
        triggerDownload(blobUrl, 'qr-code.svg', true);
      });
      return;
    }

    // JPEG / WebP — re-render the existing canvas image
    const canvas = document.createElement('canvas');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      if (format === 'jpeg') {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      ctx.drawImage(img, 0, 0);
      const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/webp';
      triggerDownload(canvas.toDataURL(mimeType, 0.95), `qr-code.${format}`);
    };
    img.src = qrCodeImage;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: { xs: 4, sm: 6 }, pb: 6, px: { xs: 2, sm: 0 } }}>
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Controls column */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 } }}>
              <Typography variant="h4" component="h1" gutterBottom align="center">
                QR Code Generator
                <IconButton
                  onClick={handleReset}
                  aria-label="Reset to default settings"
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                >
                  <RefreshIcon />
                </IconButton>
                <IconButton
                  onClick={onDarkModeToggle}
                  aria-label="Toggle dark mode"
                  sx={{ ml: 0.5, verticalAlign: 'middle' }}
                >
                  {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
              </Typography>
              <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Enter a URL to generate a QR code
              </Typography>
              <TextField
                fullWidth
                label="URL"
                variant="outlined"
                value={url}
                onChange={handleUrlChange}
                placeholder="https://example.com"
                sx={{ mb: 3 }}
                inputProps={{
                  'aria-label': 'Enter URL for QR code',
                }}
              />

              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Fun Presets</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {presets.map((preset) => (
                    <Chip
                      key={preset.name}
                      label={preset.name}
                      onClick={() => applyPreset(preset)}
                      sx={{
                        bgcolor: preset.backgroundColor,
                        color: preset.color,
                        border: `1px solid ${preset.color}`,
                        '&:hover': {
                          bgcolor: preset.color,
                          color: preset.backgroundColor,
                        },
                      }}
                    />
                  ))}
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Customize QR Code</Typography>

                <Box sx={{ mb: 2 }}>
                  <FormHelperText sx={{ mb: 1 }}>QR Color</FormHelperText>
                  <TextField
                    fullWidth
                    size="small"
                    type="color"
                    value={options.color}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleOptionChange('color', e.target.value)}
                    inputProps={{
                      'aria-label': 'QR code color',
                    }}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <FormHelperText sx={{ mb: 1 }}>Background Color</FormHelperText>
                  <TextField
                    fullWidth
                    size="small"
                    type="color"
                    value={options.backgroundColor}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleOptionChange('backgroundColor', e.target.value)}
                    disabled={options.transparent}
                    inputProps={{
                      'aria-label': 'Background color',
                    }}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={options.transparent}
                      onChange={(e) => handleOptionChange('transparent', e.target.checked)}
                      inputProps={{
                        'aria-label': 'Toggle transparent background',
                      }}
                    />
                  }
                  label="Transparent Background"
                  sx={{ mb: 2 }}
                />

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="border-style-label">Border Style</InputLabel>
                  <Select
                    labelId="border-style-label"
                    value={options.borderStyle}
                    label="Border Style"
                    onChange={(e) => handleOptionChange('borderStyle', e.target.value)}
                    inputProps={{
                      'aria-label': 'Select border style',
                    }}
                  >
                    <MenuItem value="none">No Border</MenuItem>
                    <MenuItem value="solid">Solid</MenuItem>
                    <MenuItem value="dashed">Dashed</MenuItem>
                    <MenuItem value="dotted">Dotted</MenuItem>
                    <MenuItem value="double">Double</MenuItem>
                  </Select>
                </FormControl>

                {options.borderStyle !== 'none' && (
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <FormHelperText sx={{ mb: 1 }}>Border Color</FormHelperText>
                      <TextField
                        fullWidth
                        size="small"
                        type="color"
                        value={options.borderColor}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleOptionChange('borderColor', e.target.value)}
                        inputProps={{
                          'aria-label': 'Border color',
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <FormHelperText sx={{ mb: 1 }}>Border Width (px)</FormHelperText>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={options.borderWidth}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleOptionChange('borderWidth', parseInt(e.target.value) || 20)}
                        inputProps={{
                          min: 5,
                          max: 50,
                          'aria-label': 'Border width in pixels',
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Paper>
            </Paper>
          </Box>

          {/* QR code preview column — sticky so it stays visible while scrolling */}
          <Box sx={{
            width: { xs: '100%', md: 340 },
            flexShrink: 0,
            position: { md: 'sticky' },
            top: { md: 32 },
            alignSelf: 'flex-start',
          }}>
            <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>Preview</Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  onClick={(e) => setSaveMenuAnchor(e.currentTarget)}
                  disabled={!qrCodeImage}
                >
                  Save
                </Button>
                <Menu
                  anchorEl={saveMenuAnchor}
                  open={Boolean(saveMenuAnchor)}
                  onClose={() => setSaveMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={() => downloadAs('png')}>PNG</MenuItem>
                  <MenuItem onClick={() => downloadAs('jpeg')}>JPEG</MenuItem>
                  <MenuItem onClick={() => downloadAs('webp')}>WebP</MenuItem>
                  <Divider />
                  <MenuItem onClick={() => downloadAs('svg')}>SVG (vector)</MenuItem>
                </Menu>
              </Box>
              {qrCodeImage ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: 'inline-block',
                    bgcolor: options.transparent ? 'grey.200' : 'inherit',
                    backgroundImage: options.transparent
                      ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                      : 'none',
                    backgroundSize: '20px 20px',
                    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                  }}
                >
                  <img
                    src={qrCodeImage}
                    alt="Generated QR code"
                    style={{ width: '100%', maxWidth: '280px', height: 'auto', display: 'block' }}
                  />
                </Paper>
              ) : (
                <Typography color="text.secondary" sx={{ py: 4 }}>
                  Enter a URL to see a preview
                </Typography>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default App;